import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { submitAssessment } from "@/lib/api";
import { Rocket as RocketIcon } from "lucide-react";

// Pupil-facing Exploration Test: multiple-choice, multiple answers allowed.
export default function AssessmentTest({ open, onOpenChange, project, classId, pupilId, myResult, onDone }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const questions = project?.assessment || [];

  const attemptsAllowed = project?.attemptsAllowed || 0; // 0 = unlimited
  const priorAttempts = myResult?.attempts || 0;
  const passed = !!myResult?.passed;
  const remaining = attemptsAllowed > 0 ? Math.max(0, attemptsAllowed - priorAttempts) : Infinity;
  const locked = passed || remaining <= 0;

  const toggle = (qid, oid) => {
    setAnswers((a) => {
      const cur = new Set(a[qid] || []);
      cur.has(oid) ? cur.delete(oid) : cur.add(oid);
      return { ...a, [qid]: [...cur] };
    });
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await submitAssessment(classId, project.id, pupilId, answers);
      setResult(res);
      onDone && onDone(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit — please try again.");
    }
    setBusy(false);
  };

  const close = () => {
    setResult(null);
    setAnswers({});
    setError("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent data-testid="assessment-test-dialog" className="bg-[#141738] border-[#252B5C] text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-cyan-300">🪐 Exploration Test</DialogTitle>
          <DialogDescription className="text-slate-400">
            Pick every correct answer. Score more than 40% to pass!
            {attemptsAllowed > 0 && !result && !locked && (
              <span className="block text-cyan-300/80 mt-1">Attempt {priorAttempts + 1} of {attemptsAllowed} — {remaining} left</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div data-testid="assessment-result" className="text-center py-6">
            <div className="text-6xl mb-3">{result.passed ? "🏆" : "🛰️"}</div>
            <p className="font-heading text-4xl font-bold" style={{ color: result.passed ? "#06D6A0" : "#FF6B35" }}>
              {result.percent}%
            </p>
            <p className="font-heading text-xl mt-1">{result.passed ? "You passed! 🎉" : "Keep exploring — try again!"}</p>
            <p className="text-slate-400 text-sm mt-1">{result.score} / {result.total} marks</p>
            {attemptsAllowed > 0 && !result.passed && (
              <p className="text-amber-300/90 text-sm mt-1" data-testid="assessment-remaining">
                {Math.max(0, attemptsAllowed - result.attempts)} attempt{attemptsAllowed - result.attempts === 1 ? "" : "s"} left
              </p>
            )}
            {(result.review || []).some((r) => !r.correct) && (
              <div data-testid="assessment-review" className="mt-5 text-left">
                <p className="font-heading font-bold text-amber-300 mb-2 flex items-center gap-1">🎯 What to work on</p>
                <div className="space-y-2">
                  {result.review.filter((r) => !r.correct).map((r, i) => (
                    <div key={i} data-testid={`review-wrong-${i}`} className="bg-[#0E1126] border border-red-400/30 rounded-xl p-3">
                      <p className="font-heading font-semibold text-sm">{r.text}</p>
                      <p className="text-xs mt-1 text-red-300">Your answer: {r.yourAnswers.length ? r.yourAnswers.join(", ") : "—"}</p>
                      <p className="text-xs mt-0.5 text-emerald-300">Correct: {r.correctAnswers.join(", ")}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button data-testid="assessment-close-btn" onClick={close} className="mt-5 bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px] w-full">
              Done
            </Button>
          </div>
        ) : locked ? (
          <div data-testid="assessment-locked" className="text-center py-8">
            <div className="text-6xl mb-3">{passed ? "🏆" : "🔒"}</div>
            <p className="font-heading text-2xl font-bold" style={{ color: passed ? "#06D6A0" : "#FF6B35" }}>
              {passed ? `You passed with ${myResult.percent}%!` : "No attempts left"}
            </p>
            {!passed && (
              <p className="text-slate-400 text-sm mt-1">Your best score was {myResult?.percent || 0}%.</p>
            )}
            <Button data-testid="assessment-locked-close-btn" onClick={close} className="mt-5 bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px] w-full">
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.length === 0 && <p className="text-slate-400">No questions yet.</p>}
            {questions.map((q, qi) => (
              <div key={q.id} data-testid={`test-question-${qi}`} className="bg-[#0E1126] border border-[#252B5C] rounded-xl p-3">
                <p className="font-heading font-semibold mb-1">Q{qi + 1}. {q.text} <span className="text-xs text-slate-400">({q.marks} mark{q.marks > 1 ? "s" : ""})</span></p>
                {q.image && <img src={q.image} alt="" className="rounded-lg max-h-40 mb-2" />}
                <div className="space-y-1.5">
                  {q.options.map((o, oi) => {
                    const sel = (answers[q.id] || []).includes(o.id);
                    return (
                      <button
                        key={o.id}
                        data-testid={`test-option-${qi}-${oi}`}
                        onClick={() => toggle(q.id, o.id)}
                        className={`w-full text-left flex items-center gap-2 rounded-lg p-2 border-2 transition-colors ${sel ? "border-cyan-400 bg-cyan-400/10" : "border-[#252B5C] bg-[#141738]"}`}
                      >
                        <span className={`w-5 h-5 rounded border flex items-center justify-center text-xs ${sel ? "bg-cyan-400 border-cyan-400 text-[#0B0D1B]" : "border-slate-500"}`}>{sel ? "✓" : ""}</span>
                        {o.image && <img src={o.image} alt="" className="h-8 rounded" />}
                        <span>{o.text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {error && <p data-testid="assessment-error" className="text-red-300 text-sm text-center">{error}</p>}
            {questions.length > 0 && (
              <Button data-testid="assessment-submit-btn" onClick={submit} disabled={busy} className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 min-h-[52px] font-heading font-bold">
                <RocketIcon className="w-5 h-5 mr-2" /> {busy ? "Checking…" : "Submit my answers"}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
